import { useQuery } from "@tanstack/react-query";
import {
  fetchDesigns,
  fetchServices,
  fetchLatestBlogs,
  fetchTestimonials,
} from "../api/public.api.js";
import { BRAND } from "../lib/brand.js";
import { SEO } from "../lib/seo.jsx";
import { Hero } from "../components/home/Hero.jsx";
import { LatestDesigns } from "../components/home/LatestDesigns.jsx";
import { AboutCustomize } from "../components/home/AboutCustomize.jsx";
import { ProcessSteps } from "../components/home/ProcessSteps.jsx";
import { Gallery } from "../components/home/Gallery.jsx";
import { FacadesShowcase } from "../components/home/FacadesShowcase.jsx";
import { FeaturedServices } from "../components/home/FeaturedServices.jsx";
import { LatestBlogs } from "../components/home/LatestBlogs.jsx";
import { TestimonialsCarousel } from "../components/home/TestimonialsCarousel.jsx";

/**
 * Homepage — content-managed sections (collections, services, blogs,
 * testimonials) come from the backend; everything else (hero, copy, brand
 * details) is static.
 */
export default function HomePage() {
  const latestDesignsQ = useQuery({
    queryKey: ["public", "designs", "latest"],
    queryFn: () => fetchDesigns({ limit: 3, sort: "-createdAt" }),
    staleTime: 5 * 60_000,
  });

  // Pull every published service so the homepage marquee can scroll through
  // the full lineup, not just the first three featured ones.
  const servicesQ = useQuery({
    queryKey: ["public", "services", "all"],
    queryFn: fetchServices,
    staleTime: 5 * 60_000,
  });

  const blogsQ = useQuery({
    queryKey: ["public", "blogs", "latest"],
    queryFn: () => fetchLatestBlogs(3),
    staleTime: 60_000,
  });

  const testimonialsQ = useQuery({
    queryKey: ["public", "testimonials", "featured"],
    queryFn: () => fetchTestimonials({ featured: true, limit: 6 }),
    staleTime: 5 * 60_000,
  });

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: BRAND.name,
    url: import.meta.env.VITE_SITE_URL ?? "http://localhost:5173",
    logo: BRAND.logo,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: BRAND.phone,
      contactType: "customer service",
      email: BRAND.email,
    },
    address: { "@type": "PostalAddress", streetAddress: BRAND.address },
  };

  return (
    <>
      <SEO
        title={null}
        description="Bespoke custom homes, knockdown rebuilds, and house & land packages across Melbourne."
        /* image="/home-section.jpg" */
        canonical={import.meta.env.VITE_SITE_URL ?? "http://localhost:5173"}
        schema={orgSchema}
      />

      <Hero />

      <LatestDesigns designs={latestDesignsQ.data?.items ?? []} />

      <AboutCustomize />

      <Gallery />

      <FacadesShowcase />

      <ProcessSteps />

      <FeaturedServices services={servicesQ.data ?? []} />

      <TestimonialsCarousel testimonials={testimonialsQ.data ?? []} />

      <LatestBlogs blogs={blogsQ.data ?? []} />
    </>
  );
}